import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';
import Logger from 'utils/Logger';

import { STEP_KEY } from '../utils';

import { useFunnelStorage } from './Storage';

/**
 * Accepts an object, and returns the result of that object if its values change.
 * Avoids re-rendering by comparing objects by top level values instead of by reference equality.
 *
 * Note - if the top level value is an object, it will be compared by reference equality, not by value.
 * Avoid using this hook in this case
 * TODO can we make this work for objects by using a deep comparison instead?
 */
export const useHookResult = (freshResult) => {
  const [result, setResult] = useState(freshResult);

  useEffect(
    () => setResult(freshResult),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dependency list can't be statically checked
    Object.values(freshResult),
  );

  return result;
};

export const useUpdateLocationInStorage = ({ newLocation, shouldUpdate }) => {
  const dispatch = useDispatch();
  const funnelStorage = useFunnelStorage();
  const userMarketNameState = useSelector(UserMarketName.selector);

  const [hasStarted, setHasStarted] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const reset = useCallback(() => {
    setHasStarted(false);
    setIsFinished(false);
  }, []);

  const result = useHookResult({
    hasStarted,
    isFinished,
    error: userMarketNameState?.error,
    reset,
  });

  useEffect(() => {
    if (!shouldUpdate || !newLocation?.address || isFinished) {
      return;
    }

    // First, dispatch the action to get the market name
    if (!hasStarted) {
      const { address } = newLocation;
      dispatch(UserMarketName.createActionFromAddress(address));
      setHasStarted(true);
      return;
    }

    // If the market name is not yet available, wait for it
    if (userMarketNameState.loading) {
      return;
    }

    // If an error occurs for some reason, log it.
    // We will still save the location without the market name
    // To ensure that the user can continue
    if (userMarketNameState.error) {
      Logger.error(
        'Error getting market name while updating location',
        new Error(userMarketNameState.error),
      );
    }

    // Finally, save the location with the market name
    funnelStorage.save(
      STEP_KEY.LOCATION,
      {
        ...newLocation,
        marketName: userMarketNameState?.value,
      },
      true,
    );
    setIsFinished(true);
  }, [shouldUpdate, newLocation, isFinished, userMarketNameState]);

  return result;
};
